/**
 * What a study page does when it is being closed or refreshed: send one
 * telemetry beacon, and nothing else.
 *
 * Above all it never navigates. A `beforeunload` handler that redirected to
 * Prolific turned an ordinary refresh into a submission (Craft Together
 * #341): the participant pressed F5 mid-study and found themselves on
 * Prolific's completion page with a code they had not earned. It also never
 * asks "are you sure?" — a refresh is safe here, because the server puts a
 * returning participant back where they were.
 */

export interface UnloadTarget {
  addEventListener(type: 'beforeunload' | 'pagehide', listener: () => void): void
  removeEventListener(type: 'beforeunload' | 'pagehide', listener: () => void): void
}

/**
 * Install the guard. `beacon` is called at most once per unload, and is the
 * only thing the handler does. Returns the uninstaller.
 */
export function installUnloadGuard(target: UnloadTarget, beacon: () => void): () => void {
  let sent = false
  const onLeave = (): void => {
    if (sent) return
    sent = true
    try {
      beacon()
    } catch {
      // A beacon that throws is a beacon not sent; the page still unloads.
    }
  }
  // `pagehide` too, because mobile Safari and a bfcache'd tab skip `beforeunload`.
  target.addEventListener('beforeunload', onLeave)
  target.addEventListener('pagehide', onLeave)
  return () => {
    target.removeEventListener('beforeunload', onLeave)
    target.removeEventListener('pagehide', onLeave)
  }
}
